import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, Timer, CheckCircle, ShoppingCart, CreditCard,
  FileText, AlertTriangle, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import { saveActiveVisit, clearActiveVisit } from '@/lib/useActiveVisit';
import { Visit } from '@/api/localStore';

const OUTCOMES = [
  { key: 'order', label: '🛒 تم البيع', desc: 'تم أخذ طلب' },
  { key: 'collection', label: '💰 تم التحصيل', desc: 'تم استلام مبلغ' },
  { key: 'info_only', label: '📋 معلومات', desc: 'معلومات / استبيان' },
  { key: 'no_sale', label: '❌ لم يتم البيع', desc: 'بدون نتيجة' },
  { key: 'not_available', label: '⚠️ مشكلة', desc: 'شكوى / مشكلة' },
];

const NO_SALE_REASONS = ['العميل غير موجود', 'لا احتياج حالياً', 'مشكلة في السعر', 'مخزون كافي', 'العميل غاضب', 'أخرى'];

function fmtTimer(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function CheckOutPanel({ visit, elapsed, onConfirm, saving }) {
  const [outcome, setOutcome] = useState('no_sale');
  const [noSaleReason, setNoSaleReason] = useState('');
  const [notes, setNotes] = useState('');
  const mins = Math.floor(elapsed / 60);

  return (
    <div className="space-y-4 p-4">
      {/* Summary */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <p className="font-bold text-emerald-800">ملخص الزيارة</p>
        </div>
        <p className="text-sm text-emerald-700">العميل: <strong>{visit.customer_name}</strong></p>
        <p className="text-sm text-emerald-700">المدة: <strong>{mins} دقيقة</strong></p>
      </div>

      {/* Outcome */}
      <div>
        <p className="font-bold text-foreground mb-2 text-sm">نتيجة الزيارة (مطلوب)</p>
        <div className="space-y-2">
          {OUTCOMES.map(o => (
            <button key={o.key} onClick={() => setOutcome(o.key)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-right ${outcome === o.key ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
              <div className="flex-1">
                <p className="font-semibold text-sm text-foreground">{o.label}</p>
                <p className="text-xs text-muted-foreground">{o.desc}</p>
              </div>
              {outcome === o.key && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* No-sale reason */}
      {(outcome === 'no_sale' || outcome === 'not_available') && (
        <div>
          <p className="font-semibold text-sm text-foreground mb-2">سبب عدم البيع</p>
          <div className="grid grid-cols-2 gap-2">
            {NO_SALE_REASONS.map(r => (
              <button key={r} onClick={() => setNoSaleReason(r)}
                className={`p-2.5 rounded-xl text-xs font-medium border transition-all text-right ${noSaleReason === r ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-card text-muted-foreground'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <p className="font-semibold text-sm text-foreground mb-2">ملاحظات (اختياري)</p>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="أي ملاحظات عن الزيارة..." rows={3} className="rounded-xl text-sm" />
      </div>

      <div className="flex flex-wrap gap-2">
        {['عميل راضٍ', 'نفاد منتج', 'منافسة شرسة', 'يحتاج متابعة'].map(tag => (
          <button key={tag} onClick={() => setNotes(n => n ? n + '، ' + tag : tag)}
            className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-full hover:bg-muted/70 transition-colors">
            {tag}
          </button>
        ))}
      </div>

      <Button onClick={() => onConfirm({ outcome, no_sale_reason: noSaleReason, notes })}
        disabled={saving} className="w-full h-14 bg-primary text-white text-base font-bold rounded-2xl gap-2">
        {saving ? 'جاري الحفظ...' : <><CheckCircle className="w-5 h-5" /> تأكيد إنهاء الزيارة</>}
      </Button>
    </div>
  );
}

export default function ContinueVisit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [showCheckOut, setShowCheckOut] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const startTimeRef = useRef(null);

  useEffect(() => {
    (async () => {
      const v = await Visit.filter({ id });
      const visitData = Array.isArray(v) ? v[0] : v;

      if (!visitData) {
        toast.error('الزيارة غير موجودة');
        navigate('/visits');
        return;
      }

      if (visitData.status !== 'active') {
        navigate(`/visits/${id}`);
        return;
      }

      setVisit(visitData);

      // Calculate elapsed from check_in_time
      const checkIn = new Date(visitData.check_in_time || visitData.visit_date).getTime();
      startTimeRef.current = checkIn;
      setElapsed(Math.floor((Date.now() - checkIn) / 1000));

      // Sync with localStorage so AppLayout banner stays accurate
      saveActiveVisit({
        step: 3,
        customer: { id: visitData.customer_id, name: visitData.customer_name },
        checkInData: { gps_verified: visitData.gps_verified },
        visitId: visitData.id,
        activeStartTime: checkIn,
      });

      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!startTimeRef.current) return;
    const tick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [loading]);

  const handleAction = (action) => {
    if (action === 'order') {
      navigate(`/orders/new?customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`);
    } else if (action === 'collection') {
      navigate(`/collections/new?customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`);
    }
  };

  const handleConfirmCheckOut = async ({ outcome, no_sale_reason, notes }) => {
    setSaving(true);
    const now = new Date().toISOString();
    const durationMins = Math.round(elapsed / 60);

    let score = 50;
    if (visit.gps_verified) score += 20;
    if (outcome === 'order') score += 20;
    else if (outcome === 'collection') score += 10;
    if (durationMins >= 5 && durationMins <= 45) score += 10;

    let checkoutLoc = null;
    try {
      checkoutLoc = await new Promise(res => {
        navigator.geolocation.getCurrentPosition(
          p => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => res(null), { timeout: 3000 }
        );
      });
    } catch {}

    await Visit.update(id, {
      check_out_time: now,
      status: 'completed',
      outcome,
      no_sale_reason,
      notes,
      duration_minutes: durationMins,
      checkout_latitude: checkoutLoc?.lat,
      checkout_longitude: checkoutLoc?.lng,
      visit_score: Math.min(100, score),
    });

    clearActiveVisit();
    setSaving(false);
    setSuccess(true);
  };

  if (loading) return <LoadingState message="جاري تحميل الزيارة..." />;

  // Success screen
  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 gap-6 text-center" dir="rtl">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 12 }}>
          <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-emerald-500" />
          </div>
        </motion.div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">تم تسجيل الزيارة!</h2>
          <p className="text-muted-foreground">{visit.customer_name}</p>
          <p className="text-sm text-muted-foreground">{Math.round(elapsed / 60)} دقيقة</p>
        </div>
        <div className="grid gap-3 w-full max-w-xs">
          <Button onClick={() => navigate('/visits/new')} variant="outline" className="rounded-xl h-11">زيارة جديدة</Button>
          <Button onClick={() => navigate('/visits')} className="bg-primary rounded-xl h-11">العودة للزيارات</Button>
        </div>
      </div>
    );
  }

  const ACTIONS = [
    { key: 'order', icon: ShoppingCart, label: 'أخذ طلب', color: 'bg-blue-50 border-blue-200 text-blue-700' },
    { key: 'collection', icon: CreditCard, label: 'تسجيل تحصيل', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    { key: 'not_available', icon: AlertTriangle, label: 'مشكلة/شكوى', color: 'bg-red-50 border-red-200 text-red-600' },
    { key: 'notes', icon: FileText, label: 'ملاحظات', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  ];

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 pt-3 pb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/visits')} className="p-1.5 rounded-xl hover:bg-muted transition-colors">
            <ChevronLeft className="w-5 h-5 rtl-flip" />
          </button>
          <h1 className="text-base font-bold text-foreground flex-1">زيارة جارية</h1>
          <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 font-semibold">
            🟢 نشطة
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {showCheckOut ? (
          <CheckOutPanel visit={visit} elapsed={elapsed} onConfirm={handleConfirmCheckOut} saving={saving} />
        ) : (
          <div className="p-4 space-y-4">
            {/* Live timer banner */}
            <div className="bg-primary rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs">زيارة جارية</p>
                <p className="text-white font-bold text-lg">{visit.customer_name}</p>
                {visit.gps_verified && (
                  <p className="text-white/70 text-xs flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> GPS موثق ✓
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-white/70" />
                <span className="text-2xl font-black text-white font-mono">{fmtTimer(elapsed)}</span>
              </div>
            </div>

            {/* Check-in info */}
            <div className="bg-card border border-border rounded-xl p-3">
              <p className="text-xs text-muted-foreground mb-1">سجل الزيارة</p>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-foreground">
                  تم تسجيل الوصول{' '}
                  {new Date(visit.check_in_time || visit.visit_date).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-3">
              {ACTIONS.map(a => (
                <button key={a.key}
                  onClick={() => a.key !== 'notes' && handleAction(a.key)}
                  className={`flex items-center gap-2.5 p-4 rounded-2xl border-2 text-sm font-bold transition-all active:scale-[.97] ${a.color}`}>
                  <a.icon className="w-5 h-5 flex-shrink-0" />
                  {a.label}
                </button>
              ))}
            </div>

            {/* End visit button */}
            <Button
              onClick={() => setShowCheckOut(true)}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-bold rounded-2xl gap-2 shadow-lg"
            >
              <CheckCircle className="w-5 h-5" /> إنهاء الزيارة
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}